import flow from 'lodash/flow'

import {authGet, get, monolithDel, monolithGet, monolithPost, monolithPut} from './fetch'
import {
  camelCaseObjectKeys,
  onStatus,
  shallowOmitKeys,
  shallowRenameKeys,
  shimJson,
} from './helpers'
import {
  shimCurrentEventLooksAndPeople,
  shimGetEventLooksAndPeople,
  shimEvents,
  shimParticipantLooks,
} from './eventAndLooksDataShims'
import SERVICES from '../config/services'

const urlize = (string) => (string ? `/${string}` : '')

// ///////////////////////////////
// ///// EVENT DATA //////////////
// ///////////////////////////////

/*
 * {
 *   id,
 *   date,
 *   htoArrivalDate,
 *   htoArrivalDateLabel,
 *   name,
 *   numMembers,
 *   orderId,
 *   owner,
 *   ownerRole,
 *   type,
 * }
 */
export const getCurrentEvent = () =>
  authGet(`${SERVICES.TBT_API_URL}/events/user/current`).then(
    onStatus({
      OK: shimJson(
        flow([
          camelCaseObjectKeys,
          (json) => shallowOmitKeys(json, ['estimatedDeliveryDate']),
          (json) =>
            shallowRenameKeys(json, {
              eventType: 'type',
              ownerId: 'owner',
            }),
        ]),
      ),
    }),
  )

/*
 * {
 *   group,
 *   individual,
 * }
 */
export const getSoonestEventDate = () => get(`${SERVICES.TBT_API_URL}/events/soonest-date`)

export const getCheckoutMessage = (eventId) =>
  authGet(`${SERVICES.TBT_API_URL}/events/${eventId}/checkout-messaging`).then(
    onStatus({
      OK: shimJson(camelCaseObjectKeys),
    }),
  )

const formatPayload = (payload) => ({
  email: payload.email,
  event_look_id: payload.eventLook,
  event_owner_pays: payload.ownerPays,
  first_name: payload.firstName,
  last_name: payload.lastName,
  user_id: payload.userId,
})

export const addParticipant = (payload) =>
  monolithPost(`/events/looks/${payload.eventLook}/users`, formatPayload(payload))

export const cancelParticipantOrder = (payload) =>
  monolithDel(
    `/event_look/${payload.eventLook}/participant/${payload.userId}`,
    formatPayload(payload),
  )

export const editParticipant = (payload) =>
  monolithPut(
    `/event_look/${payload.eventLook}/participant/${payload.userId}`,
    formatPayload(payload),
  ).then(
    onStatus({
      OK: shimJson(camelCaseObjectKeys),
    }),
  )

// -- Standard Calls

export const changeCurrentEvent = (eventId) => monolithPost(`/session/event/${eventId}`)

export const createNewEvent = () =>
  monolithPost('/event/create').then(
    onStatus({
      OK: shimJson(camelCaseObjectKeys),
    }),
  )

export const deleteEvent = (eventId, userId) =>
  monolithDel(`/event/${eventId}/delete`).then(
    onStatus({
      OK: shimJson(
        flow([
          camelCaseObjectKeys,
          shimCurrentEventLooksAndPeople.bind(this, userId),
          // this sets userId as an additional param of the shim, along with camelCaseObjectKeys
          // returns currentEvent, looks, and people
        ]),
      ),
    }),
  )

export const getAllEvents = (userId) =>
  monolithGet(`/events/user/${userId}`).then(
    onStatus({
      OK: shimJson(flow([camelCaseObjectKeys, shimEvents])),
    }),
  )

export const getEventAndOrderDetails = (eventId) =>
  monolithGet(`/event/${eventId}/manager`).then(
    onStatus({
      OK: shimJson(camelCaseObjectKeys),
    }),
  )

/**
 * Retrieves event transactions for the purposes of showing a receipt
 *
 * @param   {Object}  params
 * @param   {String}  params.eventId The ID of the event to retrieve transactions for
 * @param   {String}  params.userId  The ID of the user who is associated with the event
 * @returns {Promise} Returns a camel-cased server response upon success
 */
export const getEventTransactions = ({eventId, userId}) => {
  return monolithGet(`/events/${eventId}/users/${userId}/transactions`).then(
    onStatus({
      OK: shimJson(camelCaseObjectKeys),
    }),
  )
}

export const postOnboardingDetails = (eventId, payload) =>
  monolithPost(`/events/${eventId}/onboarding_registration`, payload)

export const postEventDetails = (eventId, payload) =>
  monolithPost(`/events/${eventId}`, payload).then(
    onStatus({
      OK: shimJson(camelCaseObjectKeys),
    }),
  )

export const postOwnerDetails = (eventId, payload) =>
  monolithPost(`/events/${eventId}/owner`, payload)

export const postEventMetaData = (eventId, payload) =>
  monolithPost(`/events/${eventId}/metadata`, payload)

// ///////////////////////////////
// ///// LOOK DATA ///////////////
// ///////////////////////////////

export const addToOrCreateLook = (lookId, type, payload) =>
  monolithPost(`/events/looks${urlize(lookId)}${urlize(type)}`, payload).then(
    onStatus({OK: shimJson(camelCaseObjectKeys)}),
  )

/**
 * Creates a new look for an event and receives an object of the look's details in return.
 *
 * Example structure of response:
 * {
 *    id: '8c65db56-115a-43e5-ac46-c81ce619e51d,
 *    name: 'Special Event',
 *    deleted: 0,
 *    event: 1234567,
 *    eventLookItems:[],
 *    productTotal: 0,
 *    participantIds: [246810],
 *    participants: [{}],
 *    isHto: false,
 *    hto: null
 * }
 *
 * @param {String} eventId The current event ID
 * @param {Object} payload
 * @param {String} payload.look_name The selected name of the look
 * @param {Boolean} payload.is_hto Indicates if this is an HTO look or not
 * @return {Object} Object that contains details of the new look
 *
 */
export const createEventLook = (eventId, payload) =>
  monolithPost(`/v1/events/${eventId}/looks`, payload).then(
    onStatus({
      OK: shimJson(camelCaseObjectKeys),
    }),
  )

export const getEventLooks = (eventId, userId) =>
  monolithGet(`/v1/events/${eventId}/looks`).then(
    onStatus({
      OK: shimJson(
        flow([
          camelCaseObjectKeys,
          shimGetEventLooksAndPeople.bind(this, userId),
          // this sets userId as an additional param of the shim, along with camelCaseObjectKeys
        ]),
      ),
    }),
  )

export const getParticipantLook = (eventId) =>
  monolithGet(`/events/${eventId}/participant_look_items`).then(
    onStatus({
      OK: shimJson(flow([camelCaseObjectKeys, shimParticipantLooks])),
    }),
  )

export const editLookName = (lookId, payload) => monolithPut(`/events/looks/${lookId}`, payload)

export const ownerWearingLook = (lookId, payload) =>
  monolithPut(`/events/looks/${lookId}/users`, payload)

export const deleteLook = (lookId) => monolithDel(`/events/looks/${lookId}`)

export const duplicateLook = (lookId) =>
  monolithPost(`/events/looks/${lookId}`).then(
    onStatus({
      OK: shimJson(camelCaseObjectKeys),
    }),
  )

export const deleteLookItem = (lookItemId) => monolithDel(`/events/looks/items/${lookItemId}`)

// -- Update non-paid look items
export const updateUserEventLookItem = (userEventLookItemId, payload) =>
  monolithPut(`/events/user_looks/items/${userEventLookItemId}`, payload).then(
    onStatus({
      OK: shimJson(camelCaseObjectKeys),
    }),
  )

export const updatePartnerInfo = (eventId, payload) =>
  monolithPut(`/events/${eventId}/partner-info`, payload).then(
    onStatus({
      OK: shimJson(camelCaseObjectKeys),
    }),
  )
